import { bind } from "@react-rxjs/core"
import { combineLatest, filter, map, scan, shareReplay, switchMap } from "rxjs"

import type { BarResponse } from "@/App/Equities/shared/services/TradingGateway"

import { latestBars$, watchlistSymbols$ } from "./latestQuote"
import { getHistoricalBars$ } from "./marketData"

const MS_PER_DAY = 24 * 60 * 60 * 1_000

// Ten calendar days always spans two sessions, even across a long weekend
// followed by a holiday.
const LOOKBACK_DAYS = 10

export type DaySession = {
  current: BarResponse
  previous?: BarResponse
}

export type DaySessionValues = {
  bar: BarResponse
  price: number
  change: number
  changeAmount: number
  timestamp: string
}

type PriceAndChange = Omit<DaySessionValues, "bar">

/**
 * `current` is the most recent session, `previous` the one before it.
 *
 * Mid-session `current` is today's in-progress bar; before the opening bell it
 * is the last completed session, which is what a quote screen should show until
 * trading resumes.
 */
export function lastTwoSessionsBySymbol(
  bars: BarResponse[],
): Record<string, DaySession> {
  const bySymbol = new Map<string, BarResponse[]>()

  for (const bar of bars) {
    const existing = bySymbol.get(bar.symbol)
    if (existing) {
      existing.push(bar)
    } else {
      bySymbol.set(bar.symbol, [bar])
    }
  }

  const sessions: Record<string, DaySession> = {}
  for (const [symbol, symbolBars] of bySymbol) {
    sessions[symbol] = {
      current: symbolBars[symbolBars.length - 1],
      previous: symbolBars[symbolBars.length - 2],
    }
  }

  return sessions
}

/**
 * Merges the live minute bar into the session's daily bar, updating high, low
 * and close only. Volume, open and timestamp stay as published.
 *
 * Change is measured against the previous session's close. A tick outside
 * `current`'s session is ignored, so a later day never widens an older
 * session's range.
 *
 * Pass this function's previous result back in to keep the high and low
 * running: a high already reached today must not fall.
 */
export function foldLiveIntoSession(
  { current, previous }: DaySession,
  live: BarResponse,
  previousResult: DaySessionValues | null = null,
): DaySessionValues {
  const sessionStart = Date.parse(current.t)
  const liveTime = Date.parse(live.t)

  // Bounded to this bar's own session on both sides. Without the upper bound a
  // tick from a later day widens an older session's range, which is what
  // happens whenever the current session's bar has not been published yet.
  const inSession =
    Number.isFinite(sessionStart) &&
    Number.isFinite(liveTime) &&
    liveTime >= sessionStart &&
    liveTime < sessionStart + MS_PER_DAY

  let high = inSession
    ? Math.max(Number(current.h), Number(live.h))
    : Number(current.h)
  let low = inSession
    ? Math.min(Number(current.l), Number(live.l))
    : Number(current.l)
  const price = inSession ? Number(live.c) : Number(current.c)

  // `live` only covers the current minute, so today's high has to be carried
  // forward or it drops the moment a narrower minute replaces the one that set
  // it. Keyed on the session so a new trading day starts fresh.
  if (previousResult && previousResult.bar.t === current.t) {
    high = Math.max(high, Number(previousResult.bar.h))
    low = Math.min(low, Number(previousResult.bar.l))
  }

  const previousClose = previous ? Number(previous.c) : 0
  const changeAmount = previousClose ? price - previousClose : 0

  return {
    bar: {
      ...current,
      h: String(high),
      l: String(low),
      c: String(price),
    },
    price,
    changeAmount,
    change: previousClose ? changeAmount / previousClose : 0,
    timestamp: inSession ? live.t : current.t,
  }
}

const allDaySessions$ = watchlistSymbols$.pipe(
  switchMap((symbols) => {
    const endTime = new Date()

    return getHistoricalBars$({
      symbols,
      timeframe: "1Day",
      startTime: new Date(
        endTime.getTime() - LOOKBACK_DAYS * MS_PER_DAY,
      ).toISOString(),
      endTime: endTime.toISOString(),
      // Alpaca applies `limit` across every symbol in the request, not per symbol.
      limit: symbols.split(",").length * (LOOKBACK_DAYS + 2),
    })
  }),
  map(lastTwoSessionsBySymbol),
  shareReplay({ bufferSize: 1, refCount: true }),
)

const daySessionValues$ = (symbol: string) =>
  combineLatest([
    allDaySessions$.pipe(
      map((sessions) => sessions[symbol]),
      filter((session): session is DaySession => session != null),
    ),
    latestBars$(symbol),
  ]).pipe(
    scan<[DaySession, BarResponse], DaySessionValues | null>(
      (previousResult, [session, live]) =>
        foldLiveIntoSession(session, live, previousResult),
      null,
    ),
    filter((values): values is DaySessionValues => values != null),
  )

export const [useDailyBars, dailyBars$] = bind<[string], BarResponse>(
  (symbol: string) => daySessionValues$(symbol).pipe(map(({ bar }) => bar)),
)

export const [usePriceAndChange, priceAndChange$] = bind<
  [string],
  PriceAndChange
>((symbol: string) =>
  daySessionValues$(symbol).pipe(
    map(({ price, change, changeAmount, timestamp }) => ({
      price,
      change,
      changeAmount,
      timestamp,
    })),
  ),
)
